import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { CheckIcon, SparklesIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

const PROMOTION_ACTIVE = true;  // 推广开关：你随时可以改为 false 关闭推广

const plans = [
  {
    id: 'free',
    name: '免费版',
    price: 0,
    desc: '适合个人体验和学习',
    features: [
      PROMOTION_ACTIVE ? '10 个项目（推广期间）' : '1 个项目',
      '100MB 存储空间',
      '基础组件库',
      `邮箱验证码 20 次`,
      '社区支持'
    ],
    buttonText: '免费开始',
    available: true,
    popular: false
  },
  {
    id: 'basic',
    name: '基础版',
    price: 29,
    desc: '适合个人开发者',
    features: ['3 个项目', '1GB 存储空间', '完整组件库', '邮箱验证码 5,000 次', '邮件支持', '自定义域名'],
    buttonText: '立即订阅',
    available: false,
    popular: false
  },
  {
    id: 'pro',
    name: '专业版',
    price: 99,
    desc: '适合专业团队',
    features: ['10 个项目', '10GB 存储空间', '高级组件库', '邮箱验证码 15,000 次', '优先支持', '自定义域名', '数据导出', 'API 访问'],
    buttonText: '立即订阅',
    available: false,
    popular: true
  },
  {
    id: 'enterprise',
    name: '企业版',
    price: 299,
    desc: '适合大型企业',
    features: ['无限项目', '100GB 存储空间', '全部组件库', '邮箱验证码 50,000 次', '专属客服', '私有部署', '定制开发', 'SLA 保障'],
    buttonText: '联系销售',
    available: false,
    popular: false
  }
];

export default function Pricing() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('user_token');
    if (token) {
      api.get('/auth/me')
        .then(() => setIsLoggedIn(true))
        .catch(() => setIsLoggedIn(false))
        .finally(() => setChecking(false));
    } else {
      setChecking(false);
    }
  }, []);

  const handlePlanClick = (plan) => {
    if (!plan.available) return;

    if (isLoggedIn) {
      navigate('/dashboard');
    } else {
      navigate('/register');
    }
  };

  return (
    <div className="pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            选择适合您的 <span className="gradient-text">方案</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            灵活的定价方案，满足不同规模的需求
          </p>
          {PROMOTION_ACTIVE && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-full">
              <SparklesIcon className="w-5 h-5 text-yellow-400" />
              <span className="text-yellow-300 text-sm font-medium">推广期间：免费版可创建 10 个项目！</span>
            </div>
          )}
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative p-6 rounded-2xl ${
                plan.popular
                  ? 'bg-gradient-to-b from-blue-900/50 to-purple-900/50 border-2 border-blue-500'
                  : 'bg-gray-800/50 border border-gray-700'
              } ${!plan.available ? 'opacity-60' : ''}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 gradient-bg text-white text-xs font-semibold rounded-full">
                  最受欢迎
                </div>
              )}
              
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-white mb-2">{plan.name}</h3>
                <p className="text-gray-400 text-sm mb-4">{plan.desc}</p>
                <div className="flex items-end justify-center">
                  <span className="text-4xl font-bold text-white">¥{plan.price}</span>
                  <span className="text-gray-400 ml-1">/月</span>
                </div>
              </div>
              
              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center text-gray-300 text-sm">
                    <CheckIcon className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              
              {plan.available ? (
                <button
                  onClick={() => handlePlanClick(plan)}
                  disabled={checking}
                  className={`block w-full py-3 rounded-xl text-center font-semibold transition-all ${
                    plan.popular
                      ? 'gradient-bg text-white hover:opacity-90'
                      : 'bg-blue-600 text-white hover:bg-blue-500'
                  }`}
                >
                  {isLoggedIn ? '进入工作台' : plan.buttonText}
                </button>
              ) : (
                <div className="text-center">
                  <div className="block w-full py-3 rounded-xl bg-gray-700/50 text-gray-400 font-medium text-sm cursor-not-allowed">
                    推广期间暂不开放
                  </div>
                  <p className="text-gray-500 text-xs mt-2">请先试用免费版</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div className="mt-16 text-center">
          <p className="text-gray-400 text-sm">
            所有方案均可随时升级或降级 · 推广期间注册的免费版账号将永久保留推广权益
          </p>
        </div>
      </div>
    </div>
  );
}
